/**
 * scene.llmsettings — schema_version 2，按顶部 Tab 分组持久化。
 * 打开/回滚时仅对 scene.llmsettings 做内存迁移；保存发布只写 llmsettings，不读写 settings.llm。
 */

export const LLMSETTINGS_SCHEMA_VERSION = 2

export const LLMSETTINGS_TAB_KEYS = {
  PROMPT_CONFIG: 'prompt_config',
  VOICE_SETTINGS: 'voice_settings',
  INTENT_AND_TAGS: 'intent_and_tags',
  CALL_CONTROL: 'call_control_settings',
}

/** 旧版顶层 Tab 键（schema v2 曾用 call_control，与内层 call_control 重名） */
export const LLMSETTINGS_LEGACY_CALL_CONTROL_TAB_KEY = 'call_control'

const TAB_KEYS = Object.values(LLMSETTINGS_TAB_KEYS)

/** 将旧顶层 call_control Tab 键迁移为 call_control_settings */
export function normalizeLlmsettingsTabKeys(raw) {
  const src = raw && typeof raw === 'object' ? { ...raw } : {}
  if (Number(src.schema_version) !== LLMSETTINGS_SCHEMA_VERSION) {
    return src
  }
  const legacy = src[LLMSETTINGS_LEGACY_CALL_CONTROL_TAB_KEY]
  if (legacy && typeof legacy === 'object' && !src[LLMSETTINGS_TAB_KEYS.CALL_CONTROL]) {
    src[LLMSETTINGS_TAB_KEYS.CALL_CONTROL] = legacy
    delete src[LLMSETTINGS_LEGACY_CALL_CONTROL_TAB_KEY]
  }
  return src
}

/**
 * @returns {{ valid: boolean, empty?: boolean, message?: string }}
 */
export function validateLlmsettingsSchema(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  if (!Object.keys(src).length) {
    return { valid: true, empty: true }
  }

  if (Number(src.schema_version) !== LLMSETTINGS_SCHEMA_VERSION) {
    return { valid: false }
  }

  for (let i = 0; i < TAB_KEYS.length; i += 1) {
    const key = TAB_KEYS[i]
    if (!src[key] || typeof src[key] !== 'object') {
      return { valid: false }
    }
  }

  return { valid: true, empty: false }
}

/** @returns {{ prompt_config, voice_settings, intent_and_tags, call_control_settings }} */
export function parseLlmsettingsV2(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  return {
    prompt_config: src[LLMSETTINGS_TAB_KEYS.PROMPT_CONFIG] || {},
    voice_settings: src[LLMSETTINGS_TAB_KEYS.VOICE_SETTINGS] || {},
    intent_and_tags: src[LLMSETTINGS_TAB_KEYS.INTENT_AND_TAGS] || {},
    call_control_settings: src[LLMSETTINGS_TAB_KEYS.CALL_CONTROL] || {},
  }
}

export function buildTabbedLlmsettingsDocument(parts) {
  const {
    role_setting,
    business_flow,
    custom_prompt,
    prompt_mode,
    voice_settings,
    intent_mode,
    intent_tags,
    extract_form,
    rating_rules,
    custom_tools,
    external_apis_json,
    external_apis_form,
    event_triggers,
    assistant_settings,
    customer_info_params,
  } = parts || {}

  const vs = voice_settings && typeof voice_settings === 'object' ? { ...voice_settings } : {}
  const callControlBlock = vs.call_control
  delete vs.call_control

  return {
    schema_version: LLMSETTINGS_SCHEMA_VERSION,
    [LLMSETTINGS_TAB_KEYS.PROMPT_CONFIG]: {
      prompt_mode,
      custom_prompt,
      role_setting,
      business_flow,
    },
    [LLMSETTINGS_TAB_KEYS.VOICE_SETTINGS]: vs,
    [LLMSETTINGS_TAB_KEYS.INTENT_AND_TAGS]: {
      intent_mode: intent_mode != null ? String(intent_mode) : 'regular',
      intent_tags: Array.isArray(intent_tags) ? intent_tags : [],
      extract_form:
        extract_form && typeof extract_form === 'object'
          ? {
              form_id: extract_form.form_id != null ? String(extract_form.form_id) : '',
              form_name: extract_form.form_name != null ? String(extract_form.form_name) : '',
              form_fields: Array.isArray(extract_form.form_fields) ? extract_form.form_fields : [],
            }
          : { form_id: '', form_name: '', form_fields: [] },
      rating_rules: Array.isArray(rating_rules) ? rating_rules : [],
    },
    [LLMSETTINGS_TAB_KEYS.CALL_CONTROL]: {
      call_control: callControlBlock,
      assistant_settings,
      customer_info_params,
      custom_tools: custom_tools != null ? String(custom_tools) : '',
      external_apis_json: Array.isArray(external_apis_json) ? external_apis_json : [],
      external_apis_form: Array.isArray(external_apis_form) ? external_apis_form : [],
      event_triggers: Array.isArray(event_triggers) ? event_triggers : [],
    },
  }
}
